import {
  AsYouType,
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

const E164_MAX_DIGITS = 15;

const digitLimit = (country: CountryCode): number =>
  E164_MAX_DIGITS - String(getCountryCallingCode(country)).length;

const dropTrunkPrefix = (digits: string, country: CountryCode): string => {
  if (!digits) return digits;
  const typed = new AsYouType(country);
  typed.input(digits);
  const national = typed.getNationalNumber();
  if (typed.isInternational() || !digits.endsWith(national)) return digits;
  const prefix = digits.slice(0, digits.length - national.length);
  const trunk = prefix.startsWith('0') || (getCountryCallingCode(country) === '1' && prefix === '1');
  return trunk ? national : digits;
};

export const clampNational = (raw: string, country: CountryCode): string => {
  let out = dropTrunkPrefix(raw.replace(/\D+/g, ''), country).slice(0, digitLimit(country));
  while (out.length > 1 && validatePhoneNumberLength(out, country) === 'TOO_LONG') {
    out = out.slice(0, -1);
  }
  return out;
};

export interface IPastedNumber {
  country: CountryCode;
  national: string;
}

export const readPastedNumber = (raw: string, country: CountryCode): IPastedNumber | null => {
  const parsed = parsePhoneNumberFromString(raw, country);
  if (parsed?.country && parsed.nationalNumber) {
    return { country: parsed.country, national: String(parsed.nationalNumber) };
  }
  return null;
};

export const describeNational = (national: string, country: CountryCode): string => {
  if (national.length === 0) return '';
  const dial = getCountryCallingCode(country);
  const lengthProblem = validatePhoneNumberLength(national, country);
  if (lengthProblem === 'TOO_SHORT') {
    return `That is too short for +${dial}. Enter the whole number without the country code.`;
  }
  if (lengthProblem === 'TOO_LONG') {
    return `That is too long for +${dial}.`;
  }
  const parsed = parsePhoneNumberFromString(national, country);
  if (!parsed?.isValid()) {
    return `+${dial} ${national} is not a number we can dial. Check it and try again.`;
  }
  return '';
};
